import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  unarchiveConversation,
  type ConversationDetail,
} from '@/lib/api';
import { conversationQueryKey } from './useConversation';

/**
 * useUnarchive — inverse of useMarkResolved. Calls
 * POST /pwa/conversations/:id/unresolve which removes the `pwa_resolved`
 * tag from the contact in GHL. Optimistically flips the cached
 * conversation status back to "quoted" (the most common pre-archive
 * state) so the UI restores instantly; the next refetch reconciles to
 * the contact's actual outcome.
 */
export function useUnarchive(slug: string, conversationId: string) {
  const qc = useQueryClient();
  const key = conversationQueryKey(slug, conversationId);

  return useMutation<{ resolved: false }, Error, void>({
    mutationFn: () => unarchiveConversation({ slug, conversationId }),
    onSuccess: () => {
      qc.setQueryData<ConversationDetail>(key, (curr) => {
        if (!curr) return curr;
        // We don't actually know the pre-archive status; "quoted" is the
        // most common SS workflow exit, and the conversations list query
        // will refresh and overwrite this within a beat.
        return { ...curr, status: curr.status === 'resolved' ? 'quoted' : curr.status };
      });
      void qc.invalidateQueries({ queryKey: ['conversations', slug] });
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}
